import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaFilter, FaArrowLeft, FaArrowRight, FaStar } from 'react-icons/fa';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [totalProjects, setTotalProjects] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [featuredFilter, setFeaturedFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const projectsPerPage = 6;

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        let url = `https://my-portfoli-backed.onrender.com/api/projects?page=${currentPage}&limit=${projectsPerPage}`;
        
        if (searchTerm) {
          url += `&search=${searchTerm}`;
        }
        
        if (statusFilter) {
          url += `&status=${statusFilter}`;  
        }
        
        if (featuredFilter) {
          url += `&isFeatured=true`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setProjects(data.data.projects);
        setTotalProjects(data.totalItems || data.total); // استخدام totalItems أو total حسب ما يعيده الخادم
      } catch (err) {
        console.error('Error details:', err);
        setError('حدث خطأ في جلب البيانات. تأكد من اتصال الخادم');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [searchTerm, statusFilter, featuredFilter, currentPage]);

  const totalPages = Math.ceil(totalProjects / projectsPerPage);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // العودة للصفحة الأولى عند البحث
  };

  const handleStatusFilter = (filter) => {
    setStatusFilter(filter);
    setCurrentPage(1);
  };

  const toggleFeaturedFilter = () => {
    setFeaturedFilter(!featuredFilter);
    setCurrentPage(1);
  };

  const styles = {
    container: {
      backgroundColor: '#ffffff',
      color: '#333333',
      minHeight: '100vh',
      padding: '4rem 2rem',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      lineHeight: '1.6'
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
    },
    header: {
      textAlign: 'center',
      marginBottom: '3rem',
      borderBottom: '2px solid #000000',
      paddingBottom: '1.5rem'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      color: '#000000',
      marginBottom: '0.5rem'
    },
    subtitle: {
      fontSize: '1.2rem',
      color: '#555555',
      fontWeight: '300'
    },
    controls: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      minWidth: '250px'
    },
    searchInput: {
      flex: 1,
      padding: '0.5rem 1rem',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '1rem',
      marginRight: '0.5rem'
    },
    searchButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#000',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    filterButtons: {
      display: 'flex',
      gap: '0.5rem'
    },
    filterButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#f0f0f0',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    activeFilter: {
      backgroundColor: '#000',
      color: '#fff'
    },
    projectContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '2rem',
      marginTop: '2rem'
    },
    projectCard: {
      border: '1px solid #eeeeee',
      borderRadius: '8px',
      overflow: 'hidden',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      ':hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
      }
    },
    projectImage: {
      width: '100%',
      height: '200px',
      backgroundColor: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#999999',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      position: 'relative'
    },
    featuredBadge: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      backgroundColor: '#000000',
      color: '#ffffff',
      padding: '0.3rem 0.8rem',
      borderRadius: '4px',
      fontSize: '0.8rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '0.3rem'
    },
    projectContent: {
      padding: '1.5rem'
    },
    projectTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      marginBottom: '0.75rem',
      color: '#000000'
    },
    projectDescription: {
      marginBottom: '1rem',
      color: '#555555'
    },
    projectTech: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginBottom: '1rem'
    },
    techPill: {
      backgroundColor: '#f0f0f0',
      padding: '0.3rem 0.8rem',
      borderRadius: '20px',
      fontSize: '0.8rem',
      color: '#333333'
    },
    projectLinks: {
      display: 'flex',
      gap: '1rem',
      marginTop: '1rem'
    },
    linkButton: {
      padding: '0.5rem 1rem',
      borderRadius: '4px',
      textDecoration: 'none',
      fontSize: '0.9rem',
      fontWeight: '500',
      transition: 'background-color 0.3s ease'
    },
    demoButton: {
      backgroundColor: '#000000',
      color: '#ffffff',
      ':hover': {
        backgroundColor: '#333333'
      }
    },
    codeButton: {
      backgroundColor: '#f5f5f5',
      color: '#333333',
      border: '1px solid #dddddd',
      ':hover': {
        backgroundColor: '#eeeeee'
      }
    },
    loading: {
      textAlign: 'center',
      fontSize: '1.2rem',
      padding: '2rem'
    },
    error: {
      textAlign: 'center',
      color: 'red',
      padding: '2rem'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem',
      marginTop: '2rem'
    },
    pageButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#f0f0f0',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    activePage: {
      backgroundColor: '#000',
      color: '#fff'
    },
    disabledButton: {
      opacity: '0.5',
      cursor: 'not-allowed'
    },
    noResults: {
      textAlign: 'center',
      padding: '2rem',
      gridColumn: '1 / -1'
    },
    statusBadge: {
      backgroundColor: '#f0f0f0',
      padding: '0.3rem 0.8rem',
      borderRadius: '4px',
      fontSize: '0.8rem',
      marginBottom: '1rem',
      display: 'inline-block'
    },
    completedStatus: {
      backgroundColor: '#e6f7e6',
      color: '#2e7d32'
    },
    inProgressStatus: {
      backgroundColor: '#fff8e1',
      color: '#ff8f00'
    }
  };

  if (loading) {
    return <div style={styles.loading}>جاري تحميل المشاريع...</div>;
  }

  if (error) {
    return <div style={styles.error}>حدث خطأ: {error}</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <header style={styles.header}>
          <h1 style={styles.title}>مشاريعي</h1>
          <p style={styles.subtitle}>أبرز الأعمال والتطبيقات التي قمت بتطويرها</p>
        </header>

        <div style={styles.controls}>
          <form onSubmit={handleSearch} style={styles.searchContainer}>
            <input
              type="text"
              placeholder="ابحث في المشاريع..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <button type="submit" style={styles.searchButton}>
              <FaSearch /> بحث
            </button>
          </form>

          <div style={styles.filterButtons}>
            <button
              style={{
                ...styles.filterButton,
                ...(statusFilter === 'مكتمل' ? styles.activeFilter : {})
              }}
              onClick={() => handleStatusFilter('مكتمل')}
            >
              <FaFilter /> مكتملة
            </button>
            <button
              style={{
                ...styles.filterButton,
                ...(statusFilter === 'قيد التطوير' ? styles.activeFilter : {})
              }}
              onClick={() => handleStatusFilter('قيد التطوير')}
            >
              <FaFilter /> قيد التطوير
            </button>
            <button
              style={{
                ...styles.filterButton,
                ...(featuredFilter ? styles.activeFilter : {})
              }}
              onClick={toggleFeaturedFilter}
            >
              <FaFilter /> مميزة
            </button>
            {(statusFilter || featuredFilter) && (
              <button
                style={styles.filterButton}
                onClick={() => {
                  setStatusFilter('');
                  setFeaturedFilter(false);
                }}
              >
                إلغاء الفلتر
              </button>
            )}
          </div>
        </div>

        <div style={styles.projectContainer}>
          {projects.length > 0 ? (
            projects.map((project) => (
              <div key={project._id} style={styles.projectCard}>
                <div 
                  style={{
                    ...styles.projectImage,
                    backgroundImage: project.media?.coverImage 
                      ? `url(${project.media.coverImage})` 
                      : 'none'
                  }}
                >
                  {!project.media?.coverImage && `[صورة المشروع]`}
                  {project.isFeatured && (
                    <span style={styles.featuredBadge}>
                      <FaStar /> مميز
                    </span>
                  )}
                </div>
                <div style={styles.projectContent}>
                  <h3 style={styles.projectTitle}>{project.details?.title}</h3>
                  
                  <span style={{
                    ...styles.statusBadge,
                    ...(project.status === 'مكتمل' ? styles.completedStatus : styles.inProgressStatus)
                  }}>
                    {project.status}
                  </span>
                  
                  <p style={styles.projectDescription}>
                    {project.details?.description?.substring(0, 150)}
                    {project.details?.description?.length > 150 ? '...' : ''}
                  </p>
                  
                  <div style={styles.projectTech}>
                    {project.technical?.technologies?.map((tech, index) => (
                      <span key={index} style={styles.techPill}>{tech}</span>
                    ))}
                  </div>
                  
                  <div style={styles.projectLinks}>
                    <Link 
                      to={`/projects/${project._id}`}
                      style={{...styles.linkButton, ...styles.demoButton}}
                    >
                      عرض التفاصيل
                    </Link>
                    {project.technical?.repositoryUrl && (
                      <a 
                        href={project.technical.repositoryUrl} 
                        style={{...styles.linkButton, ...styles.codeButton}}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        الكود المصدر
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={styles.noResults}>
              لا توجد مشاريع متطابقة مع معايير البحث
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div style={styles.pagination}>
            <button
              style={{
                ...styles.pageButton,
                ...(currentPage === 1 ? styles.disabledButton : {})
              }}
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <FaArrowLeft /> السابق
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                style={{
                  ...styles.pageButton,
                  ...(currentPage === i + 1 ? styles.activePage : {})
                }}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            
            <button
              style={{
                ...styles.pageButton,
                ...(currentPage === totalPages ? styles.disabledButton : {})
              }}
              onClick={() => setCurrentPage(currentPage + 1)} 
              disabled={currentPage === totalPages}
            >
              التالي <FaArrowRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;